import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, concat, Subject } from 'rxjs';
import { UpdateUserstory, Userstory } from '../models/userstory';
import { MessageService } from './message.service';
import { Task, UpdateTask } from '../models/task';
import { catchError, tap } from 'rxjs/operators';
import { Person } from '../models/person';
import { SynchronizationService } from './synchronization.service';
import { map } from 'rxjs/operators';
import { ConfigLoaderService } from '../config/configloader.service';
import { Attachable } from '../models/attachable';
import { Attachment } from '../models/attachment';
import { Comment as IssueComment } from '../models/comment';
import { Issue } from '../models/issue';
import { IssueType } from '../models/enums/issueType';
import { IssueTypeMappingUtil } from '../util/issuetype-mapping-util';
import { toJSON } from 'knockout';
import { StickerService } from './sticker.service';
import { IssueLink } from '../models/issueLink';
import { EventIssueUpdated } from '../models/syncEvents/persistenceEvents/eventIssueUpdated';
import { ActionItemIssueUpdated } from '../models/syncEvents/chatEvents/actionItemIssueUpdated';
import { EventAllIssuesMarkedAsRead } from '../models/syncEvents/persistenceEvents/eventAllIssuesMarkedAsRead';


/**
 * this class represents access to all rest requests concerning Userstory and Task data of the PyWall
 * this rest service honors isMock from settingservice and wont update data when isMock returns true
 */

@Injectable()
export class UserstoryService {

  private baseURL: string;
  private projectBaseURL: string;
  public allIssuesMarkedAsRead$ = new Subject<string>();

  constructor(
    private http: HttpClient,
    private messageService: MessageService,
    private configService: ConfigLoaderService,
    private sync: SynchronizationService,
    private stickerService: StickerService
  ) {
    this.baseURL = `${this.configService.settings.pyWallServer.url}`;
    this.projectBaseURL = `${this.baseURL}projects`;
  }


  /**
   * Loads issues of a specific project and sprint
   * @param includeSubissues if 'true' subissues will be included in the result for issues with subissues
   * @param inlcudeChangelog if 'true' changelog-information will be included for every issue (this is expensive!)
   * @param includedIssuetypes a list of issuetype-names to spefify which types should be included in the result
   * @param subissuesOrdered if 'true' subissues will be retrieved in the same order as they are in JIRA boards
   */
  // TODO
  getIssuesFromSprint(
    projectKey: string,
    sprintId: number,
    includeSubissues = false,
    includeChangelog = false,
    includedIssuetypes = 'story, task, bug, epic',
    subissuesOrdered = false): Observable<Userstory[]> {

    this.messageService.sendDebug(`UserstoryService getIssuesFromSprint called on project ${projectKey} on Sprint ${sprintId}`);
    const targetURL = this.projectBaseURL
      + `/${projectKey}/sprints/${sprintId}/issues/`
      + `?includedIssuetypes=${includedIssuetypes}`
      + `&includeSubissues=${includeSubissues}`
      + `&includeChangelog=${includeChangelog}`
      + `&subissuesOrdered=${subissuesOrdered}`;
    return this.http.get<Userstory[]>(targetURL).pipe(map(result => this.mapJSONToUserstories(result, projectKey)));
  }

  // TODO unclear why we need this for stickers, implementation in backend broken
  getIssuesFromProject(projectKey: string, provideStickers = false): Observable<Userstory[]> {
    this.messageService.sendDebug(`UserstoryService getIssues for project ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/issues?stickers=${provideStickers}/`;
    return this.http.get<Userstory[]>(targetURL).pipe(map(res => this.mapJSONToUserstories(res, projectKey)));
  }

  /**
   * this returns all userstories from the backlog of a specific project
   */
  // TODO
  getUserstoriesFromBacklog(projectKey: string, includedIssuetypes = 'story'): Observable<Userstory[]> {
    this.messageService.sendDebug(`UserstoryService getUserstoriesFromBacklog called on project ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/backlogs/?includedIssuetypes=${includedIssuetypes}`;
    return this.http.get<Userstory[]>(targetURL).pipe(map(result => this.mapJSONToUserstories(result, projectKey)));
  }

  /**
   * this returns all issues from the backlog of kanban board of a specific project
   */
  getIssuesFromKanbanBacklog(projectKey: string, filter: object = {}): Observable<Userstory[]> {
    this.messageService.sendDebug(`UserstoryService getUserstoriesFromBacklog called on project ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/backlogs/kanbans/?filter=${toJSON(filter)}`;
    return this.http.get<Userstory[]>(targetURL).pipe(map(res => this.mapJSONToUserstories(res, projectKey)));
  }

  getParentIssue(projectKey: string, issueId: number, issueKey?: string): Observable<Userstory> {
    console.log('ISSUE ID', issueId);
    this.messageService.sendDebug(`UserstoryService getParentIssue for issueId ${issueId}`);
    const issueIdentifier = (issueKey) ? issueKey : issueId;
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${issueIdentifier}/`;
    return this.http.get<Userstory>(targetURL).pipe(map(data => this.mapJSONToUserstory(data, projectKey)));
  }

  /**
   * Tries to find an issue only by its issueKey on the issuetrackingsystem that is linked
   * in the pyWall-Config to the providerProjectKey. Note that this does not have to be the
   * project the issue belongs to.
   */
  getIssueForUnknownProject(providerProjectKey: string, issueKey: string): Observable<Userstory> {
    this.messageService.sendDebug(`UserstoryService getParentIssue for issueId ${issueKey}`);
    const targetURL = this.baseURL + `${providerProjectKey}/issues/${issueKey}/`;
    return this.http.get<Userstory>(targetURL).pipe(map(data => this.mapJSONToUserstory(data, providerProjectKey)));
  }

  /**
   * Fetches and returns a subissue from jira via pywall
   */
  getSubissue(projectKey: string, subissueId: number, parentIssueId?: number): Observable<Task> {
    this.messageService.sendDebug(`UserstoryService getSubissue for subissueId ${subissueId}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${subissueId}/`;
    return this.http.get<Task>(targetURL).pipe(map(data => {
      if (parentIssueId) {
        return this.mapJSONToTask(data, parentIssueId, projectKey);
      } else {
        // TODO this sets the parentIssueId the same as taskId which is not correct
        // The underlying problem is that the pyWall-Method for GET /subtasks does not include the parentId
        // and we set it here after getting the task from pyWall
        return this.mapJSONToTask(data, subissueId, projectKey);
      }
    }));
  }

  /**
   * Fetches and returns all subissues for given parent-issue from jira via pywall
   */
  getSubissues(projectKey: string, parentIssueId: number): Observable<Task[]> {
    this.messageService.sendDebug(`UserstoryService getSubissues for parentIssueId ${parentIssueId}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${parentIssueId}/subissues/`;
    return this.http.get<Task[]>(targetURL).pipe(map(data => this.mapJSONToTasks(data, parentIssueId, projectKey)));
  }

  /**
   * Changes the order of tasks inside an issue
   * @param projectKey
   * @param storyId ID of the issue a task is reordered in
   * @param indexFrom index the task was before moving it
   * @param indexTo new index the task should be moved to
   */
  changeRankOfTask(projectKey: string, storyId: number, indexFrom: number, indexTo: number): Observable<Task> {
    this.messageService.sendDebug(`Move subtask in userstory ${storyId} from index ${indexFrom} to ${indexTo}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${storyId}/subissuerankings/`;
    const data = {
      'indexFrom': indexFrom,
      'indexTo': indexTo
    };
    return this.http.put<any>(targetURL, data);
  }

  public mapJSONToUserstories(issueData: any, projectKey?: string): Userstory[] {
    const userstories = [];
    for (const storyData of issueData) {
      const userstory = this.mapJSONToUserstory(storyData, projectKey);
      for (const taskData of storyData.subIssues) {
        const task = this.mapJSONToTask(taskData, userstory.id, projectKey);
        userstory.tasks.push(task);
      }
      userstories.push(userstory);
    }
    return userstories;
  }

  public mapJSONToUserstory(storyData: any, projectKey?: string): Userstory {
    const assignee: Person = Person.createFromPywallData(storyData.assignee);
    const reporter: Person = Person.createFromPywallData(storyData.reporter);
    const issueType: IssueType = IssueTypeMappingUtil.mapFromJiraIssueTypeName(storyData.issueType);

    let lastViewedInAwall = storyData.last_viewed_in_awall || storyData.lastViewedInAwall;
    const localLastViewed = localStorage.getItem(`awall_last_viewed_${storyData.id}`);
    if (localLastViewed) {
      if (!lastViewedInAwall || new Date(localLastViewed) > new Date(lastViewedInAwall)) {
        lastViewedInAwall = localLastViewed;
      }
    }

    if (projectKey) {
      const projectLastViewed = localStorage.getItem(`awall_project_last_viewed_${projectKey}`);
      if (projectLastViewed) {
        if (!lastViewedInAwall || new Date(projectLastViewed) > new Date(lastViewedInAwall)) {
          lastViewedInAwall = projectLastViewed;
        }
      }
    }

    if (storyData.last_viewed_in_awall) {
      console.log('Found last_viewed_in_awall for issue ' + storyData.id + ': ' + storyData.last_viewed_in_awall);
    }

    const userstory = new Userstory(
      assignee,
      storyData.description,
      storyData.estimate,
      storyData.id,
      storyData.id,
      storyData.key,
      storyData.summary,
      issueType,
      reporter,
      storyData.status,
      [],
      [],
      storyData.storyPoints,
      storyData.changelog,
      storyData.created,
      storyData.isExpedited,
      storyData.updatedAt,      // Position 17: updatedAt
      storyData.lastViewed,     // Position 18: lastViewed
      lastViewedInAwall, // Position 19: lastViewedInAwall
      storyData.issuelinks,     // Position 20: issueLinks
    );

    if (storyData.stickers !== undefined && storyData.stickers.length > 0) {
      userstory.stickers = this.stickerService.mapJSONToStickers(storyData.stickers);
    }

    return userstory;
  }

  public mapJSONToTasks(tasksData: any, parentIssueId: number, projectKey?: string): Task[] {
    const tasks = [];
    for (const taskData of tasksData) {
      const task = this.mapJSONToTask(taskData, parentIssueId, projectKey);
      tasks.push(task);
    }
    return tasks;
  }


  public mapJSONToTask(taskData: any, parentIssueId: number, projectKey?: string): Task {
    const taskAssignee: Person = Person.createFromPywallData(taskData.assignee);
    const taskReporter: Person = Person.createFromPywallData(taskData.reporter);

    let lastViewedInAwall = taskData.last_viewed_in_awall || taskData.lastViewedInAwall;
    const localLastViewed = localStorage.getItem(`awall_last_viewed_${taskData.id}`);
    if (localLastViewed) {
      if (!lastViewedInAwall || new Date(localLastViewed) > new Date(lastViewedInAwall)) {
        lastViewedInAwall = localLastViewed;
      }
    }

    if (projectKey) {
      const projectLastViewed = localStorage.getItem(`awall_project_last_viewed_${projectKey}`);
      if (projectLastViewed) {
        if (!lastViewedInAwall || new Date(projectLastViewed) > new Date(lastViewedInAwall)) {
          lastViewedInAwall = projectLastViewed;
        }
      }
    }

    const task = new Task(
      taskData.id, taskData.summary,
      taskData.description, taskData.estimate,
      taskData.time_spent,
      taskAssignee, taskReporter,
      taskData.status, parentIssueId,
      [],
      taskData.changelog,
      taskData.created,
      taskData.isExpedited,
      [],
      taskData.updatedAt,
      taskData.lastViewed,
      lastViewedInAwall
    );

    if (taskData.stickers !== undefined && taskData.stickers.length > 0) {
      task.stickers = this.stickerService.mapJSONToStickers(taskData.stickers);
    }

    return task;
  }

  public mapJSONToAttachments(data: any): Attachment[] {
    const attachments = [];
    for (const entry of data) {
      const attachment = new Attachment(entry.id, entry.fileName, entry.fileUrl, entry.mimeType);
      attachments.push(attachment);
    }
    return attachments;
  }

  /**
   * this function sends the task to the PyWall to be updated and sends an "updateTask" Synchronization Event
   * @param {string} projectKey the project the task belongs to
   * @param {Task} task the task object to send
   */
  updateTask(projectKey: string, task: Task): void {
    this.messageService.sendDebug(`Method updateTask called on project ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${task.id}`;

    // Update lastViewedInAwall before sending
    task.lastViewedInAwall = new Date().toISOString();
    localStorage.setItem(`awall_last_viewed_${task.id}`, task.lastViewedInAwall);

    this.http.patch<Task>(targetURL, new UpdateTask(task)).subscribe({
      next: () => {
        this.sync.sendPersistenceEventNew(new EventIssueUpdated(task));
        this.messageService.sendDebug(`Update Subissue ${task.id} on project ${projectKey}`);
      },
      error: (error) => {
        this.messageService.sendError(`Couldn't update Subissue ${task.id} on project ${projectKey}, ${error}`);
      }
    });
  }

  /**
  * Assignes one or more parent-issues (max. 50) to a specific sprint of the same project*.
  * The sprint must be active or open.
  * Subissues cant be assigned.
  *
   * @param sourceProjektKey *If this parameter is set the issues will be assigned to a sprint of a different
   * project. Use with caution as this deletes the issue and re-creates it in the new project and the issues
   * loses some information (i.e. history) and all its subissues.
   */
  // TODO sprintId should be a number
  assignIssuesToSprint(projectKey: string, sprintId: string, userStories: Userstory[], sourceProjektKey?: string): Observable<Userstory[]> {
    this.messageService.sendDebug('Assign issue(s) to sprint ' + sprintId);
    let targetURL = this.projectBaseURL + `/${projectKey}/sprints/${sprintId}/issues/`;
    if (sourceProjektKey) {
      targetURL += `?source_project_key=${sourceProjektKey}`;
    }
    const keys = userStories.map(x => x.key);
    return this.http.post<Userstory[]>(targetURL, { 'issues': keys }).pipe(tap(
      () => {
        userStories.forEach(story => this.sync.sendPersistenceEventNew(new EventIssueUpdated(story, Number(sprintId))));
      }), catchError(this.handleError<Userstory[]>('assignIssuesToSprint'))
    );
  }

  /**
   * Adds user stories to the product backlog of the specified project
   * @param projectKey
   * @param userStories
   */
  addUserStoriesToProductBacklog(projectKey: string, userStories: Userstory[]): Observable<Userstory[]> {
    this.messageService.sendDebug(`addUserStoryToProductBacklog called on project ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/backlogs/issues/`;
    const keys = userStories.map(userStory => userStory.key);
    const data = {
      'issues': keys
    };
    return this.http.post<Userstory[]>(targetURL, data);
  }

  /**
   * create a new issue with the passed setting
   * @param projectKey Create
   * @param type
   * @param name
   * @param description
   * @param assignee
   * @param reporter
   * @param statusId
   * @param sprintId
   * @param storyPoints
   * @returns
   */
  createIssue(
    projectKey: string,
    type: IssueType,
    name: string,
    description = '',
    assignee?: Person,
    reporter?: Person,
    statusId?: number,
    sprintId?: number,
    storyPoints?: number
  ): Observable<Userstory> {
    const targetURL = this.projectBaseURL + `/${projectKey}/issues`;
    const issueTypeName = IssueTypeMappingUtil.mapToJiraIssueTypeName(type);
    const data = { 'issuetype': issueTypeName, 'summary': name, 'description': description };

    if (assignee) {
      data['assignee'] = assignee;
    }
    if (reporter) {
      data['reporter'] = reporter;
    }
    if (statusId) {
      data['status'] = {
        'id': statusId
      };
    }
    if (sprintId) {
      data['sprintId'] = sprintId;
    }
    if (storyPoints && type === IssueType.USERSTORY) {
      data['storyPoints'] = storyPoints;
    }
    return this.http.post<Userstory>(targetURL, data)
      .pipe(
        tap((story: Userstory) => this.messageService.sendDebug(`created userstory with id: ${story.id}`)),
        catchError(this.handleError<Userstory>('createUserstory'))
      );
  }


  // TODO would be more elegant if we get the subtask back directly instead of only the id
  /**
   * Creates a subissue for the specified parentIssue
   * */
  createSubissue(
    projectName: string,
    parentIssueId: number,
    summary: string,
    description: string,
    assignee?: Person,
    reporter?: Person,
    statusId?: number
  ): Observable<any> {
    this.messageService.sendDebug(`createSubissue called on project ${projectName}`);

    const targetURL = this.projectBaseURL + `/${projectName}/issues/` + parentIssueId + '/subissues/';
    const data = {
      'summary': summary,
      'parent_issue_id': parentIssueId,
      'description': description,
      'issuetype': 'Sub-task'
    };
    if (assignee) {
      data['assignee'] = assignee;
    }
    if (reporter) {
      data['reporter'] = reporter;
    }
    if (statusId) {
      data['status'] = {
        'id': statusId
      };
    }
    const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    return this.http.post<any>(targetURL, data, { headers });
  }

  /**
   * Changes the rank of all stories (affectedUserstories) that rank before and after
   * the referenceStory as to insert the referenceStory at a specific index/rank among affectedUserstories
   */
  changeRank(projectKey: string, referenceStory: Userstory, affectedUserStories: Userstory[]): Observable<Userstory> {
    const indexOfReferenceUserStory: number = affectedUserStories.indexOf(referenceStory);
    const userStoriesBefore: Userstory[] = affectedUserStories.slice(0, indexOfReferenceUserStory);
    const userStoriesAfter: Userstory[] = affectedUserStories.slice(indexOfReferenceUserStory + 1);
    const changeRankBefore = this.changeRankOfUserstories(projectKey, userStoriesBefore, referenceStory, true);
    const changeRankAfter = this.changeRankOfUserstories(projectKey, userStoriesAfter, referenceStory, false);
    return concat(changeRankBefore, changeRankAfter);
  }

  /**
   * This function is used to change the rank of user stories and sends an event to the sync server to update
   * other clients.
   */
  changeRankOfUserstories(projectKey: string, userstories: Userstory[], referenceUserstory: Userstory,
    before: boolean): Observable<Userstory> {

    const data = {
      'issues': userstories.map((story) => story.key),
      'reference_issue': referenceUserstory.key,
      'position': (before) ? 'Before' : 'After'
    };

    this.messageService.sendDebug(`changeRankOfUserstories called on project ${projectKey}`);

    const targetURL = this.projectBaseURL + `/${projectKey}/issueRankings/`;
    this.sync.sendPersistenceEvent('changeRankOfUserstory', referenceUserstory);
    return this.http.put<Userstory>(targetURL, data);
  }

  updateIssue(projectKey: string, issueId: number, issue: Userstory, excludeSelf?: boolean, callBack?: () => any): void {
    this.messageService.sendDebug(`Method updateIssue called on project ${projectKey}`);
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${issueId}`;

    // Update lastViewedInAwall before sending
    issue.lastViewedInAwall = new Date().toISOString();
    localStorage.setItem(`awall_last_viewed_${issueId}`, issue.lastViewedInAwall);

    this.http.patch<Userstory>(targetURL, new UpdateUserstory(issue)).subscribe({
      next: (updatedIssue) => {
        this.sync.sendPersistenceEventNew(new EventIssueUpdated(issue), excludeSelf);
        this.sync.sendChatEvent(new ActionItemIssueUpdated(updatedIssue), false);
        this.messageService.sendDebug(`Update Issue ${issueId} on project ${projectKey}`);
        if(callBack) {
          callBack();
        }
      },
      error: (error) => {
        this.messageService.sendError(`Couldn't update Issue ${issueId} on project ${projectKey}, ${error}`);
      }
    });
  }

  /**
   * Get outward IssueLinks from the issue with key = issueKey.
   *
   * If 'getReverseLinks' is set to 'true' this will get the INWARD links, meaning the links of other
   * issues linking to the issue with key = issueKey
   *
   */
  getIssueLinks(issueKey: string, projectKey: string, getReverseLinks = false): Observable<IssueLink[]> {


    const targetURL = this.baseURL + `projects/${projectKey}/issuedependencies/?issueKey=${issueKey}`;
    return this.http.get<IssueLink[]>(targetURL).pipe(map(data => this.mapJSONToIssueLink(data, getReverseLinks)));
  }

  /**
   * @param getReverseLinks if true will return inward issuelinks instead of outward
   */
  public mapJSONToIssueLink(data: any, getReverseLinks: boolean): IssueLink[] {
    const result: IssueLink[] = [];
    const dataLinks = (getReverseLinks) ? data.linkedToByIssues : data.issueLinks;
    for (const entry of dataLinks) {
      const link = new IssueLink(entry.id, entry.issueKey, entry.issueId);
      result.push(link);
    }
    return result;
  }

  /**
   * Creates a new outward issuelink in sourceIssue.
   * On success a persistence-event will be created for the updated issue containing the new issuelink
   */
  createIssueLink(sourceIssueKey: string, sourceProjectKey: string, targetIssueKey: string): Observable<any> {
    const targetURL = this.baseURL + 'projects/{sourceProjectKey}/issuedependencies/';
    const dependencyType = 'Blocks';
    // this can be read as a json object with the corresponding properties
    const payload = { sourceIssueKey, targetIssueKey, dependencyType };

    return this.http.post(targetURL, payload).pipe(
      tap({
        complete: () => {
          this.getIssueForUnknownProject(sourceProjectKey, sourceIssueKey).subscribe((updatedIssue: Issue) => {
            this.sync.sendPersistenceEventNew(new EventIssueUpdated(updatedIssue), false);
          });
        }
      }),
      catchError(this.handleError<any>('createIssueLink'))
    );
  }

  deleteIssueLink(issueLinkId: number, sourceProjectKey: string): void {
    const targetURL = this.baseURL + 'projects/{sourceProjectKey}/issuedependencies/' + issueLinkId + '/';
    this.http.delete(targetURL).subscribe({
      error: (error) => {
        this.messageService.sendError(`Couldn't delete Issue Link ${issueLinkId} on project ${sourceProjectKey}, ${error.message}`);
      }
    });
  }

  setAssigneeForIssue(projectKey: string, issue: Issue, assignee: Person): void {
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${issue.id}/assignees/`;

    this.http.put(targetURL, { userId: assignee['id'] }).subscribe({
      next: () => {
        this.sync.sendPersistenceEventNew(new EventIssueUpdated(issue));
      },
      error: (error) => {
        this.messageService.sendWarn(`Couldn't assign ${assignee.displayName} to issue ${issue.id}, ${error}`);
      }
    });
  }

  addAttachmentToIssue(projectKey: string, issueId: number, attachment: Attachable): Observable<any> {
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${issueId}/attachments/`;
    return this.http.post(targetURL, {
      'attachmentId': attachment.id,
      'attachmentType': attachment.type,
      'fileName': attachment.fileName
    });
  }

  getAttachmentsForIssue(projectKey: string, issueId: number): Observable<Attachment[]> {
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${issueId}/attachments/`;
    return this.http.get<Attachment[]>(targetURL).pipe(map(data => this.mapJSONToAttachments(data)));
  }

  getCommentsForIssue(projectKey: string, issueId: number): Observable<IssueComment[]> {
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${issueId}/comments/`;
    return this.http.get(targetURL).pipe(map(data => {
      const comments: IssueComment[] = [];
      (<Array<string>>data).forEach(entry => {
        const comment = IssueComment.createFromPywallData(entry);
        comments.push(comment);
      });
      return comments;
    }));
  }

  setLastViewed(projectKey: string, issueId: number): Observable<any> {
    const now = new Date().toISOString();
    localStorage.setItem(`awall_last_viewed_${issueId}`, now);

    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${issueId}/viewed`;
    const headers = new HttpHeaders().set('X-Skip-Interceptor', 'true');
    return this.http.put(targetURL, {
      'last_viewed_in_awall': now,
      'lastViewedInAwall': now
    }, { headers }).pipe(catchError(error => of(null)));
  }

  markAllAsRead(projectKey: string): Observable<any> {
    const now = new Date().toISOString();
    localStorage.setItem(`awall_project_last_viewed_${projectKey}`, now);

    this.allIssuesMarkedAsRead$.next(now);
    // this.sync.sendPersistenceEventNew(new EventAllIssuesMarkedAsRead(now));

    const targetURL = this.projectBaseURL + `/${projectKey}/issues/viewed`;
    const headers = new HttpHeaders().set('X-Skip-Interceptor', 'true');
    return this.http.put(targetURL, {
      'last_viewed_in_awall': now,
      'lastViewedInAwall': now
    }, { headers }).pipe(
      catchError(error => {
        console.warn('Could not persist mark all as read', error);
        return of(null);
      })
    );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.messageService.sendError(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  /**
   * This deletes an issue and all subtasks it might have (!) in jira.
   */
  deleteIssue(id: number, projectKey: string): Observable<any> {
    const targetURL = this.projectBaseURL + `/${projectKey}/issues/${id}/`;
    this.messageService.sendDebug('deleted issue #' + id);
    return this.http.delete<any>(targetURL);
  }

  /**
   * New issues that are not yet persisted to JIRA get a temporary id.
   * We need an id to identify the issue across multiple clients for sync-actions.
   * The temporary id is a negative number to easily distinguish it from a real/persistent id.
   * To get a unique id across multiple clients we use the current time millisec as id. This is of course
   * not -guaranteed- unique but the probability should be very high.
   */
  getTemporaryIdForNewIssue(): number {
    const tempId: number = new Date().getTime() * (-1);
    return tempId;
  }
}
